import * as React from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp, Clock } from 'lucide-react';

import type {
  AdminListQueryDto,
  AdminListSortColumn,
  AdminListSortDirection,
  EstablishmentStateDto,
  TokenActivationStatusDto,
} from '@/api/supplier-client';
import type { SupplierParticipationSummaryDto } from '@/api/participations-client';
import { useCampaignsList, useCampaignDetail } from '@/api/use-campaigns';
import { useCguSignedStatus } from '@/api/use-legal-admin';
import { useParticipationsSummary } from '@/api/use-participations';
import { useSuppliersList } from '@/api/use-suppliers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet } from '@/components/ui/sheet';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  establishmentStateLabel,
  tokenActivationStatusFilterLabel,
} from '@/lib/supplier-labels';
import { cn } from '@/lib/utils';
import { CguSignatureCell } from '@/routes/admin/cgu/cgu-signature-status';
import { SupplierDetailScreen } from '@/routes/admin/suppliers/supplier-detail-screen';

// =============================================================================
// suppliers-list-screen.tsx — Écran A-03 « Registre des fournisseurs » (T2-REGISTRE).
// =============================================================================
// GET /admin/establishments PAGINÉ (read-model D5, enveloppe {items,total,page,pageSize}). Le registre
// devient un TABLEAU DE TRAVAIL admin : recherche (dénomination/SIREN/SIRET), filtres (statut d'établis-
// sement, période d'activité MASTER, campagne), tri sur colonnes OWNED, pagination. L'état de VUE
// (recherche/filtres/tri/page) vit dans les SEARCH PARAMS d'URL (RULE_REF_006 : PAS de localStorage),
// porté par l'adaptateur routeur (suppliers-list-route.tsx) via `view`/`onChangeView`. Le composant
// reste testable hors routeur (props contrôlées).
//
// COLONNES COMPOSÉES DANS LE FRONT (isolation BC — jamais un import ni une requête cross-schéma) :
//   - « Participations » + « Dernière date de référencement » : POST /admin/campaigns/participations/
//     summary avec les SIRET de la PAGE courante (useParticipationsSummary) — merge PAR SIRET.
//   - « Signature CGU » (S5bis) : endpoint admin legal cross-fournisseur (useCguSignedStatus).
//   - Filtre « campagne » : GET /admin/campaigns/:id → participations[].supplierSiret → param `sirets`
//     du registre (le serveur n'interroge JAMAIS campaign — SIRET opaques).
//
// HORLOGE (T2) : `activationStatus` est DÉRIVÉ SERVEUR (MASTER de la ligne). L'UI rend l'icône horloge
// DOUBLÉE d'un LIBELLÉ TEXTE (« Lien expiré ») ssi `activationStatus === 'Expire'` — jamais une couleur
// seule (WCAG 2.1 AA). Les autres statuts (Envoyé/Activé/—) n'affichent pas d'horloge.
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope> ; en-têtes de tri = <button> + aria-sort ; barre
// d'outils role="search" avec <label>/<legend> liés ; états rendus en LIBELLÉ texte (badge = renfort) ;
// messages role="status"/role="alert" ; pagination role="navigation" ; navigation clavier native.
//
// R22-FICHE-DRAWER (GATE_HUMAN, DBU §2.2 l.626/l.686) : l'action « Consulter la fiche » d'une ligne
// ouvre la fiche A-04 dans un VOLET LATÉRAL (Sheet), PAS une navigation plein écran — le registre reste
// MONTÉ et visible en arrière-plan pendant la consultation. `openSiret`/`onCloseDetail` sont CONTRÔLÉS
// par l'adaptateur routeur (état d'URL, search param `open` ; RULE_REF_006) ; le composant réutilise
// TEL QUEL le contenu de fiche existant (SupplierDetailScreen, écran A-04 plein écran conservé pour le
// deep-link direct `/admin/suppliers/$siret`).

// --- Contrat d'état de vue (porté par l'URL via l'adaptateur routeur) ---------

/** Colonnes triables du registre = colonnes OWNED serveur (garde de désérialisation du param `sort`). */
export const SUPPLIER_SORT_COLUMNS: readonly AdminListSortColumn[] = [
  'siren',
  'raisonSocialeLegale',
  'siret',
  'etablissementState',
  'masterLastActivityAt',
];

/** Les 6 états d'établissement (chips de filtre statut — GATE A). */
export const ESTABLISHMENT_STATES: readonly EstablishmentStateDto[] = [
  'Brouillon',
  'Soumis',
  'Refuse',
  'Actif',
  'Suspendu',
  'Archive',
];

/**
 * Statuts d'activation filtrables (chips « Statut d'activation », multi). Ordre d'affichage GATE_HUMAN
 * « Activé / En attente / Expiré ». Sert d'allowlist de désérialisation du param `tokenStatus` (URL).
 */
export const TOKEN_ACTIVATION_STATUSES: readonly TokenActivationStatusDto[] = [
  'Active',
  'Envoye',
  'Expire',
];

/** Tailles de page proposées (sélecteur pageSize ; borne serveur = 100). */
export const SUPPLIER_PAGE_SIZES: readonly number[] = [10, 25, 50, 100];

/** État de VUE du registre (recherche/filtres/tri/pagination). Vit dans l'URL (RULE_REF_006). */
export interface SuppliersRegistryView {
  /** Recherche texte libre (dénomination OU SIREN OU SIRET) ; '' = aucune. */
  readonly q: string;
  /** Filtre statut d'établissement (multi) ; [] = tous. */
  readonly states: readonly EstablishmentStateDto[];
  /** Filtre statut d'ACTIVATION du lien MASTER (multi ∈ {Active,Envoye,Expire}) ; [] = tous. */
  readonly tokenStatuses: readonly TokenActivationStatusDto[];
  /** Borne basse de période (date civile 'YYYY-MM-DD') sur l'activité MASTER ; '' = aucune. */
  readonly from: string;
  /** Borne haute de période ; '' = aucune. */
  readonly to: string;
  /** Campagne sélectionnée (résolue en `sirets`) ; '' = aucune. */
  readonly campaignId: string;
  readonly sort: AdminListSortColumn;
  readonly dir: AdminListSortDirection;
  /** Page courante (1-based). */
  readonly page: number;
  readonly pageSize: number;
}

/** Vue par défaut : tout affiché, tri SIRET ascendant, page 1, 25 par page. */
export const DEFAULT_SUPPLIERS_VIEW: SuppliersRegistryView = {
  q: '',
  states: [],
  tokenStatuses: [],
  from: '',
  to: '',
  campaignId: '',
  sort: 'siret',
  dir: 'asc',
  page: 1,
  pageSize: 25,
};

export interface SuppliersListScreenProps {
  /** Ouvre la fiche A-04 d'un SIRET dans le VOLET LATÉRAL (état porté par l'adaptateur routeur). */
  readonly onOpen: (siret: string) => void;
  /** Ouvre l'écran d'enrôlement A-05 (navigation portée par l'adaptateur routeur). */
  readonly onEnroll: () => void;
  /**
   * T7-MULTI-ETAB — ouvre la vue GROUPÉE des établissements d'un SIREN (personne morale).
   * OPTIONNEL : quand fourni, la valeur SIREN du registre devient cliquable (entry-point NAV
   * additif, plan ARCH §2.3) ; absent, le SIREN reste un texte simple (rétro-compat T2).
   */
  readonly onOpenHierarchy?: (siren: string) => void;
  /** État de vue (URL) ; défaut = vue complète non filtrée. */
  readonly view?: SuppliersRegistryView;
  /** Notifie le routeur d'un changement de vue (écrit l'état d'URL). */
  readonly onChangeView?: (next: SuppliersRegistryView) => void;
  /** SIRET actuellement ouvert dans le volet latéral (état d'URL) ; `null`/absent = volet fermé. */
  readonly openSiret?: string | null;
  /** Ferme le volet latéral (touche Échap, clic sur le fond, bouton Fermer/Retour). */
  readonly onCloseDetail?: () => void;
}

export function SuppliersListScreen({
  onOpen,
  onEnroll,
  onOpenHierarchy,
  view = DEFAULT_SUPPLIERS_VIEW,
  onChangeView,
  openSiret = null,
  onCloseDetail,
}: SuppliersListScreenProps): React.JSX.Element {
  const closeDetail = React.useCallback((): void => {
    onCloseDetail?.();
  }, [onCloseDetail]);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Référentiel fournisseurs
          </h1>
          <p className="text-muted-foreground">
            Établissements référencés. Recherchez, filtrez et triez le registre,
            ou enrôlez un nouvel établissement.
          </p>
        </div>
        <Button type="button" onClick={onEnroll}>
          Enrôler un fournisseur
        </Button>
      </header>

      <SuppliersRegistry
        view={view}
        onChangeView={onChangeView}
        onOpen={onOpen}
        onOpenHierarchy={onOpenHierarchy}
      />

      {/* R22-FICHE-DRAWER : volet latéral superposé — le registre CI-DESSUS reste MONTÉ pendant que
          la fiche est consultée (jamais démonté, contrairement à l'ancienne navigation plein écran). */}
      <Sheet open={openSiret !== null} onClose={closeDetail} label="Fiche fournisseur">
        {openSiret !== null ? (
          <SupplierDetailScreen siret={openSiret} onBack={closeDetail} />
        ) : null}
      </Sheet>
    </div>
  );
}

// --- Formatage --------------------------------------------------------------

/** Formate un horodatage ISO 8601 en date/heure locale FR. « — » si `null` ; brut si ISO invalide. */
function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Formate une date de référencement (jour) en date FR ; « — » si `null`. */
function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Classe partagée des <select> natifs (tokens sémantiques ; focus visible clavier). */
const SELECT_CLASS = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

// --- Registre : recherche + filtres + tri + pagination (état serveur) --------

function SuppliersRegistry({
  view,
  onChangeView,
  onOpen,
  onOpenHierarchy,
}: {
  readonly view: SuppliersRegistryView;
  readonly onChangeView: ((next: SuppliersRegistryView) => void) | undefined;
  readonly onOpen: (siret: string) => void;
  readonly onOpenHierarchy: ((siren: string) => void) | undefined;
}): React.JSX.Element {
  const emit = React.useCallback(
    (next: SuppliersRegistryView): void => {
      onChangeView?.(next);
    },
    [onChangeView],
  );

  // Filtre « campagne » : résolution des SIRET du périmètre (GET /admin/campaigns/:id), GATÉE sur
  // la sélection d'une campagne (aucun appel si `campaignId` vide).
  const campaignSelected = view.campaignId !== '';
  const campaignDetail = useCampaignDetail(view.campaignId, {
    enabled: campaignSelected,
  });
  const campaignSirets: readonly string[] | undefined =
    campaignSelected && campaignDetail.data !== undefined
      ? campaignDetail.data.participations.map((p) => p.supplierSiret)
      : undefined;

  // Critères serveur (query params). Le filtre campagne entre par `sirets` (SIRET opaques).
  const query: AdminListQueryDto = {
    ...(view.q !== '' ? { q: view.q } : {}),
    ...(view.states.length > 0 ? { state: view.states } : {}),
    ...(view.tokenStatuses.length > 0 ? { tokenStatus: view.tokenStatuses } : {}),
    ...(view.from !== '' ? { from: view.from } : {}),
    ...(view.to !== '' ? { to: view.to } : {}),
    ...(campaignSirets !== undefined ? { sirets: campaignSirets } : {}),
    sort: view.sort,
    dir: view.dir,
    page: view.page,
    pageSize: view.pageSize,
  };

  const list = useSuppliersList(query);
  // Statut de signature CGU par SIRET (endpoint admin legal cross-fournisseur, S5bis).
  const signedStatus = useCguSignedStatus();

  // Colonnes participations : POST summary avec les SIRET de la PAGE courante uniquement (≤ pageSize).
  const pageSirets = React.useMemo(
    () => (list.data?.items ?? []).map((r) => r.siret),
    [list.data],
  );
  const participations = useParticipationsSummary(pageSirets);
  const summaryBySiret = React.useMemo(() => {
    const map = new Map<string, SupplierParticipationSummaryDto>();
    for (const row of participations.data ?? []) {
      map.set(row.supplierSiret, row);
    }
    return map;
  }, [participations.data]);

  const onSort = React.useCallback(
    (column: AdminListSortColumn): void => {
      const nextDir: AdminListSortDirection =
        view.sort === column && view.dir === 'asc' ? 'desc' : 'asc';
      emit({ ...view, sort: column, dir: nextDir, page: 1 });
    },
    [emit, view],
  );

  if (list.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des fournisseurs…
      </p>
    );
  }
  if (list.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {list.error.message}
      </p>
    );
  }

  const { items, total, pageSize } = list.data;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(view.page, 1), totalPages);
  const hasActiveFilters =
    view.q !== '' ||
    view.states.length > 0 ||
    view.tokenStatuses.length > 0 ||
    view.from !== '' ||
    view.to !== '' ||
    campaignSelected;

  return (
    <div className="space-y-4">
      <SuppliersToolbar view={view} emit={emit} />

      {items.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          {hasActiveFilters
            ? 'Aucun fournisseur ne correspond aux critères de recherche.'
            : 'Aucun fournisseur référencé pour le moment.'}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Registre des fournisseurs référencés : SIREN, raison sociale, SIRET,
                état d'activation, signature CGU, dernière activité du maître,
                participations et dernière date de référencement. Les colonnes marquées
                d'un en-tête bouton sont triables.
              </caption>
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                  <SortableHeader column="siren" label="SIREN" view={view} onSort={onSort} />
                  <SortableHeader
                    column="raisonSocialeLegale"
                    label="Raison sociale"
                    view={view}
                    onSort={onSort}
                  />
                  <SortableHeader column="siret" label="SIRET" view={view} onSort={onSort} />
                  <SortableHeader
                    column="etablissementState"
                    label="État"
                    view={view}
                    onSort={onSort}
                  />
                  <th scope="col" className="px-4 py-3 font-medium">
                    Signature CGU
                  </th>
                  {/* DBU §2.2 l.676 « date de dernière connexion » — A2 réel (masterLastLoginAt),
                      DISPLAY-only (non SQL-triable, parité activationStatus) → th plain non triable. */}
                  <th scope="col" className="px-4 py-3 font-medium">
                    Dernière connexion
                  </th>
                  {/* Colonnes composées (campaign) — non triables server-side. */}
                  <th scope="col" className="px-4 py-3 font-medium">
                    Participations
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Dernière date de référencement
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const state = establishmentStateLabel(row.etablissementState);
                  const summary = summaryBySiret.get(row.siret);
                  return (
                    <tr
                      key={row.siret}
                      className="border-b border-border last:border-b-0"
                    >
                      {/* T7-MULTI-ETAB : SIREN cliquable vers la vue groupée si onOpenHierarchy
                          est fourni (entry-point NAV additif) ; sinon texte simple (rétro-compat). */}
                      <td className="px-4 py-3 align-top font-mono">
                        {onOpenHierarchy !== undefined ? (
                          <button
                            type="button"
                            onClick={() => onOpenHierarchy(row.siren)}
                            aria-label={`Voir la hiérarchie du SIREN ${row.siren}`}
                            className={cn(
                              'rounded-sm font-mono text-primary underline underline-offset-2',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            )}
                          >
                            {row.siren}
                          </button>
                        ) : (
                          row.siren
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {row.raisonSocialeLegale ?? (
                          <span className="text-muted-foreground">Non renseignée</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top font-mono">{row.siret}</td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-col gap-1">
                          {/* Badge = renfort ; le LIBELLÉ texte porte l'information. */}
                          <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                          {/* HORLOGE (T2) : icône DÉCORATIVE + LIBELLÉ TEXTE (jamais couleur seule). */}
                          {row.activationStatus === 'Expire' ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                              <Clock aria-hidden="true" className="h-3.5 w-3.5" />
                              <span>Lien d'activation expiré</span>
                            </span>
                          ) : null}
                        </div>
                      </td>
                      {/* Signature CGU — composée front : statut par SIRET (endpoint admin legal). */}
                      <td className="px-4 py-3 align-top">
                        <CguSignatureCell status={signedStatus.data?.[row.siret]} />
                      </td>
                      {/* DBU §2.2 l.676 « date de dernière connexion » — A2 réel (masterLastLoginAt) ; « — » si jamais connecté. */}
                      <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                        {formatDateTime(row.masterLastLoginAt)}
                      </td>
                      {/* Participations (composée front, POST summary) — compteur PAR SIRET. */}
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {summary === undefined ? '—' : String(summary.participationCount)}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                        {formatDate(summary?.lastReferencedAt)}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onOpen(row.siret)}
                          aria-label={`Consulter la fiche du fournisseur ${row.siret}`}
                        >
                          Consulter la fiche
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <nav
            aria-label="Pagination du registre fournisseurs"
            className="flex items-center justify-between gap-4"
          >
            <p role="status" className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages} — {total} fournisseur
              {total > 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2">
              <Label htmlFor="suppliers-page-size" className="text-sm">
                Par page
              </Label>
              <select
                id="suppliers-page-size"
                className={cn(SELECT_CLASS, 'h-9 w-auto')}
                value={view.pageSize}
                onChange={(event) =>
                  emit({ ...view, pageSize: Number(event.target.value), page: 1 })
                }
              >
                {SUPPLIER_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => emit({ ...view, page: currentPage - 1 })}
              >
                Précédent
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => emit({ ...view, page: currentPage + 1 })}
              >
                Suivant
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}

// --- Barre d'outils : recherche (debounced) + chips filtres -------------------

function SuppliersToolbar({
  view,
  emit,
}: {
  readonly view: SuppliersRegistryView;
  readonly emit: (next: SuppliersRegistryView) => void;
}): React.JSX.Element {
  const campaigns = useCampaignsList();

  // Recherche DEBOUNCED (~300 ms) : état local d'input, émission différée vers l'URL (ENF-01).
  const [qInput, setQInput] = React.useState(view.q);
  // Resynchronise si l'URL change de l'extérieur (deep-link / retour navigateur).
  React.useEffect(() => {
    setQInput(view.q);
  }, [view.q]);
  // Ref vers la dernière fonction d'émission : l'effet de debounce ne dépend QUE de `qInput`.
  const emitSearchRef = React.useRef<(q: string) => void>(() => undefined);
  emitSearchRef.current = (q: string): void => {
    if (q !== view.q) {
      emit({ ...view, q, page: 1 });
    }
  };
  React.useEffect(() => {
    const timer = setTimeout(() => emitSearchRef.current(qInput), 300);
    return () => clearTimeout(timer);
  }, [qInput]);

  const toggleState = (state: EstablishmentStateDto): void => {
    const next = view.states.includes(state)
      ? view.states.filter((s) => s !== state)
      : [...view.states, state];
    emit({ ...view, states: next, page: 1 });
  };

  const toggleTokenStatus = (status: TokenActivationStatusDto): void => {
    const next = view.tokenStatuses.includes(status)
      ? view.tokenStatuses.filter((s) => s !== status)
      : [...view.tokenStatuses, status];
    emit({ ...view, tokenStatuses: next, page: 1 });
  };

  return (
    <div role="search" aria-label="Recherche et filtres du registre" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5 lg:col-span-2">
          <Label htmlFor="suppliers-search">
            Rechercher (dénomination, SIREN ou SIRET)
          </Label>
          <Input
            id="suppliers-search"
            type="search"
            value={qInput}
            placeholder="Ex. BOULANGERIE, 552100554 ou 55210055400013"
            onChange={(event) => setQInput(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="suppliers-from">Activité depuis</Label>
          <Input
            id="suppliers-from"
            type="date"
            value={view.from}
            onChange={(event) => emit({ ...view, from: event.target.value, page: 1 })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="suppliers-to">Activité jusqu'au</Label>
          <Input
            id="suppliers-to"
            type="date"
            value={view.to}
            onChange={(event) => emit({ ...view, to: event.target.value, page: 1 })}
          />
        </div>

        <div className="flex flex-col gap-1.5 lg:col-span-2">
          <Label htmlFor="suppliers-campaign">Campagne</Label>
          <select
            id="suppliers-campaign"
            className={SELECT_CLASS}
            value={view.campaignId}
            onChange={(event) =>
              emit({ ...view, campaignId: event.target.value, page: 1 })
            }
          >
            <option value="">Toutes les campagnes</option>
            {(campaigns.data ?? []).map((c) => (
              <option key={c.campaignId} value={c.campaignId}>
                {c.intitule}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Chips de filtre statut (multi) — boutons bascule accessibles (aria-pressed). */}
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="mb-1 text-sm font-medium text-muted-foreground">
          Filtrer par état d'activation
        </legend>
        {ESTABLISHMENT_STATES.map((state) => {
          const active = view.states.includes(state);
          const label = establishmentStateLabel(state).label;
          return (
            <button
              key={state}
              type="button"
              aria-pressed={active}
              onClick={() => toggleState(state)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted',
              )}
            >
              {label}
            </button>
          );
        })}
      </fieldset>

      {/* Chips de filtre STATUT D'ACTIVATION (multi) — même pattern a11y (aria-pressed). Le libellé
          texte porte l'info du filtre (WCAG) ; composition résolue SERVEUR (le front n'envoie que le param). */}
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="mb-1 text-sm font-medium text-muted-foreground">
          Filtrer par statut d'activation
        </legend>
        {TOKEN_ACTIVATION_STATUSES.map((status) => {
          const active = view.tokenStatuses.includes(status);
          const label = tokenActivationStatusFilterLabel(status);
          return (
            <button
              key={status}
              type="button"
              aria-pressed={active}
              onClick={() => toggleTokenStatus(status)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted',
              )}
            >
              {label}
            </button>
          );
        })}
      </fieldset>
    </div>
  );
}

/** En-tête de colonne triable : <th aria-sort> + <button> déclencheur (clavier natif). */
function SortableHeader({
  column,
  label,
  view,
  onSort,
}: {
  readonly column: AdminListSortColumn;
  readonly label: string;
  readonly view: SuppliersRegistryView;
  readonly onSort: (column: AdminListSortColumn) => void;
}): React.JSX.Element {
  const active = view.sort === column;
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? view.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const Icon = !active ? ChevronsUpDown : view.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th scope="col" aria-sort={ariaSort} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 rounded-sm font-medium',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <span>{label}</span>
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}
